/**
 * Builds a channel-uploadable HTML5 creative bundle from a rendered ad image:
 * a ZIP containing an `index.html` shell (with the `ad.size` meta tag and
 * `clickTag` exit wiring that Google Ads / CM360 / DV360 validators require)
 * plus the ad image itself.
 *
 * Google Ads enforces a hard 150 KB limit on the *zipped* bundle, so the
 * builder steps the image down a JPEG quality ladder until the archive fits,
 * preferring the lossless PNG when it already does. A bundle that still
 * doesn't fit at the lowest quality is returned flagged `overBudget` so
 * callers can warn instead of silently shipping a creative the channel will
 * reject.
 */

import { injectXmpIntoJpeg } from "@hightouch/lib/ad-studio/disclaimer/jpeg-metadata";
import { buildAiDisclosureXmp } from "@hightouch/lib/ad-studio/disclaimer/xmp";
import { createZipBlob } from "src/utils/download";

/** Google Ads display HTML5 creatives: 150 KB max, measured on the ZIP. */
export const HTML5_BUNDLE_MAX_ZIPPED_BYTES = 150 * 1024;

/**
 * Highest-first quality steps tried when the PNG doesn't fit. 0.35 is the
 * floor — below that, photographic backgrounds degrade beyond what's usable
 * in a paid placement, so we stop and report over-budget instead.
 */
const JPEG_QUALITY_LADDER = [0.85, 0.75, 0.65, 0.55, 0.45, 0.35] as const;

const PNG_IMAGE_FILENAME = "ad.png";
const JPEG_IMAGE_FILENAME = "ad.jpg";

export type Html5BundleImage = {
  /** PNG at exactly the creative's spec dimensions (the capture pipeline's output). */
  blob: Blob;
  width: number;
  height: number;
};

export type Html5BundleResult = {
  /** The zipped bundle (`index.html` + image). */
  blob: Blob;
  zippedBytes: number;
  imageType: "image/png" | "image/jpeg";
  /** JPEG quality used, or null when the lossless PNG fit the budget. */
  quality: number | null;
  /** True when even the lowest-quality attempt exceeds `maxZippedBytes`. */
  overBudget: boolean;
};

/**
 * The `index.html` shell. Kept to the minimum the channel validators look
 * for: `ad.size` declares the fixed creative dimensions, and the exit click
 * must route through the `clickTag` global — the serving platform injects the
 * landing URL into that variable at serve time, so it ships empty here.
 */
export function buildBundleIndexHtml(args: {
  width: number;
  height: number;
  imageFilename: string;
}): string {
  const { width, height, imageFilename } = args;
  return [
    "<!DOCTYPE html>",
    '<html lang="en">',
    "<head>",
    '<meta charset="utf-8">',
    `<meta name="ad.size" content="width=${width},height=${height}">`,
    "<title>Ad</title>",
    '<script type="text/javascript">var clickTag = "";</script>',
    "<style>",
    "html,body{margin:0;padding:0}",
    `#ad{display:block;position:relative;width:${width}px;height:${height}px;overflow:hidden;cursor:pointer}`,
    "#ad img{display:block;width:100%;height:100%;border:0}",
    "</style>",
    "</head>",
    "<body>",
    '<a id="ad" href="javascript:window.open(window.clickTag)">',
    `<img src="${imageFilename}" alt="">`,
    "</a>",
    "</body>",
    "</html>",
  ].join("\n");
}

/**
 * Core bundle builder with the encoder injected so the quality ladder is
 * testable without a canvas. Browser callers should use
 * `buildHtml5BundleFromPng` instead.
 */
export async function buildHtml5Bundle(args: {
  image: Html5BundleImage;
  /** Re-encode the source image as JPEG at `quality`; null when encoding fails. */
  reencodeToJpeg: (quality: number) => Promise<Blob | null>;
  maxZippedBytes?: number;
}): Promise<Html5BundleResult> {
  const {
    image,
    reencodeToJpeg,
    maxZippedBytes = HTML5_BUNDLE_MAX_ZIPPED_BYTES,
  } = args;

  const zipCandidate = async (
    imageBlob: Blob,
    imageType: Html5BundleResult["imageType"],
    quality: number | null,
  ): Promise<Html5BundleResult> => {
    const imageFilename =
      imageType === "image/png" ? PNG_IMAGE_FILENAME : JPEG_IMAGE_FILENAME;
    const indexHtml = buildBundleIndexHtml({
      width: image.width,
      height: image.height,
      imageFilename,
    });
    const blob = await createZipBlob([
      {
        filename: "index.html",
        blob: new Blob([indexHtml], { type: "text/html" }),
      },
      { filename: imageFilename, blob: imageBlob },
    ]);
    return {
      blob,
      zippedBytes: blob.size,
      imageType,
      quality,
      overBudget: blob.size > maxZippedBytes,
    };
  };

  let candidate = await zipCandidate(image.blob, "image/png", null);
  if (!candidate.overBudget) return candidate;

  for (const quality of JPEG_QUALITY_LADDER) {
    const jpeg = await reencodeToJpeg(quality);
    if (!jpeg) break;
    candidate = await zipCandidate(jpeg, "image/jpeg", quality);
    if (!candidate.overBudget) return candidate;
  }

  return candidate;
}

/**
 * Browser entry point: decodes the capture pipeline's PNG for its exact
 * dimensions and wires up a canvas-based JPEG re-encoder for the ladder.
 *
 * `machineReadableDisclosure` re-injects the IPTC/XMP "AI generated" tag into
 * every JPEG rung: the source PNG carries it in its metadata (see
 * `addAiDisclosureMetadataToPng`), but canvas re-encoding produces a fresh
 * JPEG with no metadata — without this, shrinking the bundle to budget would
 * silently strip a disclosure the user explicitly opted into.
 */
export async function buildHtml5BundleFromPng(
  pngBlob: Blob,
  opts?: { maxZippedBytes?: number; machineReadableDisclosure?: boolean },
): Promise<Html5BundleResult> {
  const bitmap = await createImageBitmap(pngBlob);
  try {
    return await buildHtml5Bundle({
      image: { blob: pngBlob, width: bitmap.width, height: bitmap.height },
      reencodeToJpeg: async (quality) => {
        const jpeg = await encodeBitmapAsJpeg(bitmap, quality);
        if (!jpeg || !opts?.machineReadableDisclosure) return jpeg;
        return addAiDisclosureMetadataToJpeg(jpeg);
      },
      maxZippedBytes: opts?.maxZippedBytes,
    });
  } finally {
    bitmap.close();
  }
}

/** Re-encodes a captured PNG as JPEG, carrying the AI disclosure tag over when requested. */
export async function convertPngToJpeg(
  pngBlob: Blob,
  opts: { quality: number; machineReadableDisclosure?: boolean },
): Promise<Blob> {
  const bitmap = await createImageBitmap(pngBlob);
  try {
    const jpeg = await encodeBitmapAsJpeg(bitmap, opts.quality);
    if (!jpeg) throw new Error("convertPngToJpeg: JPEG encoding failed");
    return opts.machineReadableDisclosure
      ? addAiDisclosureMetadataToJpeg(jpeg)
      : jpeg;
  } finally {
    bitmap.close();
  }
}

async function addAiDisclosureMetadataToJpeg(blob: Blob): Promise<Blob> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  // Re-wrap so the local type is ArrayBuffer-backed (the helper's return type
  // widens to ArrayBufferLike, which BlobPart rejects).
  const withMetadata = new Uint8Array(
    injectXmpIntoJpeg(bytes, buildAiDisclosureXmp()),
  );
  return new Blob([withMetadata], { type: "image/jpeg" });
}

async function encodeBitmapAsJpeg(
  bitmap: ImageBitmap,
  quality: number,
): Promise<Blob | null> {
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  // JPEG has no alpha channel; composite over white so any transparent
  // regions in the source PNG don't encode as black.
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bitmap, 0, 0);
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
  });
}
